import type { Spell } from '@character-creator/types';

export const level6Spells: Spell[] = [
  {
    id: 'blade-barrier',
    name: 'Blade Barrier',
    level: 6,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: true,
    description: "You create a vertical wall of whirling, razor-sharp blades made of magical energy. The wall appears within range and lasts for the duration. You can make a straight wall up to 100 feet long, 20 feet high, and 5 feet thick, or a ringed wall up to 60 feet in diameter, 20 feet high, and 5 feet thick. The wall provides three-quarters cover to creatures behind it, and its space is difficult terrain. When a creature enters the wall's area for the first time on a turn or starts its turn there, the creature must make a Dexterity saving throw. On a failed save, the creature takes 6d10 slashing damage. On a successful save, the creature takes half as much damage.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'chain-lightning',
    name: 'Chain Lightning',
    level: 6,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of fur; a piece of amber, glass, or a crystal rod; and three silver pins' },
    duration: 'Instantaneous',
    concentration: false,
    description: "You create a bolt of lightning that arcs toward a target of your choice that you can see within range. Three bolts then leap from that target to as many as three other targets, each of which must be within 30 feet of the first target. A target can be a creature or an object and can be targeted by only one of the bolts. A target must make a Dexterity saving throw. The target takes 10d8 lightning damage on a failed save, or half as much damage on a successful one.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, one additional bolt leaps from the first target to another target for each slot level above 6th.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'circle-of-death',
    name: 'Circle of Death',
    level: 6,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'the powder of a crushed black pearl worth at least 500 gp', materialCost: 500 },
    duration: 'Instantaneous',
    concentration: false,
    description: "A sphere of negative energy ripples out in a 60-foot-radius sphere from a point within range. Each creature in that area must make a Constitution saving throw. A target takes 8d6 necrotic damage on a failed save, or half as much damage on a successful one.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, the damage increases by 2d6 for each slot level above 6th.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'conjure-fey',
    name: 'Conjure Fey',
    level: 6,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 minute',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "You summon a fey creature of challenge rating 6 or lower, or a fey spirit that takes the form of a beast of challenge rating 6 or lower. It appears in an unoccupied space that you can see within range. The fey creature disappears when it drops to 0 hit points or when the spell ends. The fey creature is friendly to you and your companions for the duration.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, the challenge rating increases by 1 for each slot level above 6th.",
    classes: ['druid', 'warlock'],
    source: 'srd-5.1',
  },
  {
    id: 'contingency',
    name: 'Contingency',
    level: 6,
    school: 'evocation',
    ritual: false,
    castingTime: '10 minutes',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a statuette of yourself carved from ivory and decorated with gems worth at least 1,500 gp', materialCost: 1500 },
    duration: '10 days',
    concentration: false,
    description: "Choose a spell of 5th level or lower that you can cast, that has a casting time of 1 action, and that can target you. You cast that spell—called the contingent spell—as part of casting contingency, expending spell slots for both, but the contingent spell doesn't come into effect. Instead, it takes effect when a certain circumstance occurs. You describe that circumstance when you cast the two spells.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'create-undead',
    name: 'Create Undead',
    level: 6,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 minute',
    range: '10 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'one clay pot filled with grave dirt, one clay pot filled with brackish water, and one 150 gp black onyx stone for each corpse', materialCost: 150 },
    duration: 'Instantaneous',
    concentration: false,
    description: "You can cast this spell only at night. Choose up to three corpses of Medium or Small humanoids within range. Each corpse becomes a ghoul under your control. As a bonus action on each of your turns, you can mentally command any creature you made with this spell if the creature is within 120 feet of you.",
    higherLevel: "When you cast this spell using a 7th-level spell slot, you can animate or reassert control over four ghouls. When you cast this spell using an 8th-level spell slot, you can animate or reassert control over five ghouls or two ghasts or wights. When you cast this spell using a 9th-level spell slot, you can animate or reassert control over six ghouls, three ghasts or wights, or two mummies.",
    classes: ['cleric', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'disintegrate',
    name: 'Disintegrate',
    level: 6,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a lodestone and a pinch of dust' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A thin green ray springs from your pointing finger to a target that you can see within range. The target can be a creature, an object, or a creation of magical force, such as the wall created by wall of force. A creature targeted by this spell must make a Dexterity saving throw. On a failed save, the target takes 10d6 + 40 force damage. The target is disintegrated if this damage leaves it with 0 hit points. A disintegrated creature and everything it is wearing and carrying, except magic items, are reduced to a pile of fine gray dust.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, the damage increases by 3d6 for each slot level above 6th.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'eyebite',
    name: 'Eyebite',
    level: 6,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "For the spell's duration, your eyes become an inky void imbued with dread power. One creature of your choice within 60 feet of you that you can see must succeed on a Wisdom saving throw or be affected by one of the following effects of your choice for the duration: Asleep, Panicked, or Sickened. Each turn until the spell ends, you can use your action to target another creature but can't target a creature again if it has succeeded on a saving throw against this casting of eyebite.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'find-the-path',
    name: 'Find the Path',
    level: 6,
    school: 'divination',
    ritual: false,
    castingTime: '1 minute',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a set of divinatory tools—such as bones, ivory sticks, cards, teeth, or carved runes—worth 100 gp and an object from the location you wish to find', materialCost: 100 },
    duration: '1 day',
    concentration: true,
    description: "This spell allows you to find the shortest, most direct physical route to a specific fixed location that you are familiar with on the same plane of existence. If you name a destination on another plane of existence, a destination that moves (such as a mobile fortress), or a destination that isn't specific (such as \"a green dragon's lair\"), the spell fails. For the duration, as long as you are on the same plane of existence as the destination, you know how far it is and in what direction it lies.",
    classes: ['bard', 'cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'flesh-to-stone',
    name: 'Flesh to Stone',
    level: 6,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of lime, water, and earth' },
    duration: '1 minute',
    concentration: true,
    description: "You attempt to turn one creature that you can see within range into stone. If the target's body is made of flesh, the creature must make a Constitution saving throw. On a failed save, it is restrained as its flesh begins to harden. On a successful save, the creature isn't affected. A creature restrained by this spell must make another Constitution saving throw at the end of each of its turns. If it successfully saves against this spell three times, the spell ends. If it fails its saves three times, it is turned to stone and subjected to the petrified condition for the duration.",
    classes: ['warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'forbiddance',
    name: 'Forbiddance',
    level: 6,
    school: 'abjuration',
    ritual: true,
    castingTime: '10 minutes',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a sprinkling of holy water, rare incense, and powdered ruby worth at least 1,000 gp', materialCost: 1000 },
    duration: '1 day',
    concentration: false,
    description: "You create a ward against magical travel that protects up to 40,000 square feet of floor space to a height of 30 feet above the floor. For the duration, creatures can't teleport into the area or use portals to enter the area. The spell proofs the area against planar travel, and therefore prevents creatures from accessing the area by way of the Astral Plane, Ethereal Plane, Feywild, Shadowfell, or the plane shift spell.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'globe-of-invulnerability',
    name: 'Globe of Invulnerability',
    level: 6,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (10-foot radius)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a glass or crystal bead that shatters when the spell ends' },
    duration: '1 minute',
    concentration: true,
    description: "An immobile, faintly shimmering barrier springs into existence in a 10-foot radius around you and remains for the duration. Any spell of 5th level or lower cast from outside the barrier can't affect creatures or objects within it, even if the spell is cast using a higher level spell slot. Such a spell can target creatures and objects within the barrier, but the spell has no effect on them. Similarly, the area within the barrier is excluded from the areas affected by such spells.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, the barrier blocks spells of one level higher for each slot level above 6th.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'guards-and-wards',
    name: 'Guards and Wards',
    level: 6,
    school: 'abjuration',
    ritual: false,
    castingTime: '10 minutes',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'burning incense, a small measure of brimstone and oil, a knotted string, a small amount of umber hulk blood, and a small silver rod worth at least 10 gp', materialCost: 10 },
    duration: '24 hours',
    concentration: false,
    description: "You create a ward that protects up to 2,500 square feet of floor space (an area 50 feet square, or one hundred 5-foot squares or twenty-five 10-foot squares). The warded area can be up to 20 feet tall, and shaped as you desire. You can ward several stories of a stronghold by dividing the area among them, as long as you can walk into each contiguous area while you are casting the spell. When you cast this spell, you can specify individuals that are unaffected by any or all of the effects that you choose.",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'harm',
    name: 'Harm',
    level: 6,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You unleash a virulent disease on a creature that you can see within range. The target must make a Constitution saving throw. On a failed save, it takes 14d6 necrotic damage, or half as much damage on a successful save. The damage can't reduce the target's hit points below 1. If the target fails the saving throw, its hit point maximum is reduced for 1 hour by an amount equal to the necrotic damage it took. Any effect that removes a disease allows a creature's hit point maximum to return to normal before that time passes.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'heal',
    name: 'Heal',
    level: 6,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "Choose a creature that you can see within range. A surge of positive energy washes through the creature, causing it to regain 70 hit points. This spell also ends blindness, deafness, and any diseases affecting the target. This spell has no effect on constructs or undead.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, the amount of healing increases by 10 for each slot level above 6th.",
    classes: ['cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'heroes-feast',
    name: "Heroes' Feast",
    level: 6,
    school: 'conjuration',
    ritual: false,
    castingTime: '10 minutes',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a gem-encrusted bowl worth at least 1,000 gp, which the spell consumes', materialCost: 1000, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "You bring forth a great feast, including magnificent food and drink. The feast takes 1 hour to consume and disappears at the end of that time, and the beneficial effects don't set in until this hour is over. Up to twelve creatures can partake of the feast. A creature that partakes of the feast gains several benefits including being cured of diseases and poison, becoming immune to poison and being frightened, and having advantage on Wisdom saving throws. Its hit point maximum also increases by 2d10, and it gains the same number of hit points. These benefits last for 24 hours.",
    classes: ['cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'magic-jar',
    name: 'Magic Jar',
    level: 6,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 minute',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a gem, crystal, reliquary, or some other ornamental container worth at least 500 gp', materialCost: 500 },
    duration: 'Until dispelled',
    concentration: false,
    description: "Your body falls into a catatonic state as your soul leaves it and enters the container you used for the spell's material component. While your soul inhabits the container, you are aware of your surroundings as if you were in the container's space. You can't move or use reactions. The only action you can take is to project your soul up to 100 feet out of the container, either returning to your living body (and ending the spell) or attempting to possess a humanoids body.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'mass-suggestion',
    name: 'Mass Suggestion',
    level: 6,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: false, material: true, materialDescription: "a snake's tongue and either a bit of honeycomb or a drop of sweet oil" },
    duration: '24 hours',
    concentration: false,
    description: "You suggest a course of activity (limited to a sentence or two) and magically influence up to twelve creatures of your choice that you can see within range and that can hear and understand you. Creatures that can't be charmed are immune to this effect. The suggestion must be worded in such a manner as to make the course of action sound reasonable.",
    higherLevel: "When you cast this spell using a 7th-level spell slot, the duration is 10 days. When you use an 8th-level spell slot, the duration is 30 days. When you use a 9th-level spell slot, the duration is a year and a day.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'move-earth',
    name: 'Move Earth',
    level: 6,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'an iron blade and a small bag containing a mixture of soils—clay, loam, and sand' },
    duration: '2 hours',
    concentration: true,
    description: "Choose an area of terrain no larger than 40 feet on a side within range. You can reshape dirt, sand, or clay in the area in any manner you choose for the duration. You can raise or lower the area's elevation, create or fill in a trench, erect or flatten a wall, or form a pillar. The extent of any such changes can't exceed half the area's largest dimension. So, if you affect a 40-foot square, you can create a pillar up to 20 feet high, raise or lower the square's elevation by up to 20 feet, dig a trench up to 20 feet deep, and so on.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'otiluke-freezing-sphere',
    name: "Freezing Sphere",
    level: 6,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '300 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small crystal sphere' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A frigid globe of cold energy streaks from your fingertips to a point of your choice within range, where it explodes in a 60-foot-radius sphere. Each creature within the area must make a Constitution saving throw. On a failed save, a creature takes 10d6 cold damage. On a successful save, it takes half as much damage. If the globe strikes a body of water or a liquid that is principally water (not including water-based creatures), it freezes the liquid to a depth of 6 inches over an area 30 feet square. This ice lasts for 1 minute.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, the damage increases by 1d6 for each slot level above 6th.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'planar-ally',
    name: 'Planar Ally',
    level: 6,
    school: 'conjuration',
    ritual: false,
    castingTime: '10 minutes',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You beseech an otherworldly entity for aid. The being must be known to you: a god, a primordial, a demon prince, or some other being of cosmic power. That entity sends a celestial, an elemental, or a fiend loyal to it to aid you, making the creature appear in an unoccupied space within range. If you know a specific creature's name, you can speak that name when you cast this spell to request that creature, though you might get a different creature anyway (DM's choice).",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'programmed-illusion',
    name: 'Programmed Illusion',
    level: 6,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of fleece and jade dust worth at least 25 gp', materialCost: 25 },
    duration: 'Until dispelled',
    concentration: false,
    description: "You create an illusion of an object, a creature, or some other visible phenomenon within range that activates when a specific condition occurs. The illusion is imperceptible until then. It must be no larger than a 30-foot cube, and you decide when you cast the spell how the illusion behaves and what sounds it makes. This scripted performance can last up to 5 minutes.",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'sunbeam',
    name: 'Sunbeam',
    level: 6,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (60-foot line)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a magnifying glass' },
    duration: '1 minute',
    concentration: true,
    description: "A beam of brilliant light flashes out from your hand in a 5-foot-wide, 60-foot-long line. Each creature in the line must make a Constitution saving throw. On a failed save, a creature takes 6d8 radiant damage and is blinded until your next turn. On a successful save, it takes half as much damage and isn't blinded by this spell. Undead and oozes have disadvantage on this saving throw. You can create a new line of radiance as your action on any turn until the spell ends.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'transport-via-plants',
    name: 'Transport via Plants',
    level: 6,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '10 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 round',
    concentration: false,
    description: "This spell creates a magical link between a Large or larger inanimate plant within range and another plant, at any distance, on the same plane of existence. You must have seen or touched the destination plant at least once before. For the duration, any creature can step into the target plant and exit from the destination plant by using 5 feet of movement.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'true-seeing',
    name: 'True Seeing',
    level: 6,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'an ointment for the eyes that costs 25 gp; is made from mushroom powder, saffron, and fat; and is consumed by the spell', materialCost: 25, materialConsumed: true },
    duration: '1 hour',
    concentration: false,
    description: "This spell gives the willing creature you touch the ability to see things as they actually are. For the duration, the creature has truesight, notices secret doors hidden by magic, and can see into the Ethereal Plane, all out to a range of 120 feet.",
    classes: ['bard', 'cleric', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'wall-of-ice',
    name: 'Wall of Ice',
    level: 6,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small piece of quartz' },
    duration: '10 minutes',
    concentration: true,
    description: "You create a wall of ice on a solid surface within range. You can form it into a hemispherical dome or a sphere with a radius of up to 10 feet, or you can shape a flat surface made up of ten 10-foot-square panels. Each panel must be contiguous with another panel. In any form, the wall is 1 foot thick and lasts for the duration. If the wall cuts through a creature's space when it appears, the creature within its area is pushed to one side of the wall and must make a Dexterity saving throw. On a failed save, the creature takes 10d6 cold damage, or half as much damage on a successful save.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, the damage the wall deals when it appears increases by 2d6, and the damage from passing through the sheet of frigid air increases by 1d6, for each slot level above 6th.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'wall-of-thorns',
    name: 'Wall of Thorns',
    level: 6,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a handful of thorns' },
    duration: '10 minutes',
    concentration: true,
    description: "You create a wall of tough, pliable, tangled brush bristling with needle-sharp thorns. The wall appears within range on a solid surface and lasts for the duration. You choose to make the wall up to 60 feet long, 10 feet high, and 5 feet thick or a circle that has a 20-foot diameter and is up to 20 feet high and 5 feet thick. The wall blocks line of sight. When the wall appears, each creature within its area must make a Dexterity saving throw. On a failed save, a creature takes 7d8 piercing damage, or half as much damage on a successful save.",
    higherLevel: "When you cast this spell using a spell slot of 7th level or higher, both types of damage increase by 1d8 for each slot level above 6th.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'wind-walk',
    name: 'Wind Walk',
    level: 6,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 minute',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'fire and holy water' },
    duration: '8 hours',
    concentration: false,
    description: "You and up to ten willing creatures you can see within range assume a gaseous form for the duration, appearing as wisps of cloud. While in this cloud form, a creature has a flying speed of 300 feet and has resistance to damage from nonmagical weapons. The only actions a creature can take in this form are the Dash action or to revert to its normal form.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'word-of-recall',
    name: 'Word of Recall',
    level: 6,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '5 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You and up to five willing creatures within 5 feet of you instantly teleport to a previously designated sanctuary. You and any creatures that teleport with you appear in the nearest unoccupied space to the spot you designated when you prepared your sanctuary. If you cast this spell without first preparing a sanctuary, the spell has no effect.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
];
